import React from "react";
import blog2 from "../../assets/blogs/blog_2.png";
import blog4 from "../../assets/blogs/blog_4.png";
import featuredAi from "../../assets/insights/featured-ai-decisions.jpg";
import sustainableFuture from "../../assets/insights/sustainable-future.jpg";
import climateWeek from "../../assets/insights/climate-week.jpg";
import houstonStartup from "../../assets/insights/houston-startup.png";
import awsLab from "../../assets/insights/aws-genai-lab.jpg";
import ceraWeek from "../../assets/insights/ceraweek-speaker.png";
import newsMain from "../../assets/news/dsider_main_news.png";
import news1 from "../../assets/news/recent_news_1.png";
import news2 from "../../assets/news/recent_news_2.png";
import news3 from "../../assets/news/recent_news_3.png";

// `body` is either a string (blank-line / newline separated paragraphs) or JSX.
// `source` is an optional external link (LinkedIn post) shown under the article.
export const POSTS = [
  {
    id: "ai-decision-multiplier",
    type: "Blog",
    featured: true,
    image: featuredAi,
    title: "How AI Is Changing the Way Leaders Make Decisions",
    excerpt:
      "The true value of AI for leadership is not found in “stopwatch gains” (saving time on manual tasks), but in its ability to serve as a Decision Multiplier.",
    body: (
      <>
        <p>
          <strong>Hypothesis</strong>
          <br />
          The true value of AI for leadership is not found in “stopwatch gains”
          (saving time on manual tasks), but in its ability to serve as a{" "}
          <strong>Decision Multiplier</strong>. By integrating end-to-end
          business context with institutional memory, AI allows leaders to move
          from reactive instinct to proactive, economically sound strategy.
        </p>

        <h2>The Legacy of the “Gut Call”</h2>
        <p>
          Not long ago, most leadership decisions followed a familiar pattern:
          review last quarter’s numbers, talk to a few trusted people, apply
          experience, and make a call.
        </p>
        <p>
          Data mattered, but it arrived late and often raised more questions
          than answers. It was a “rear-view mirror” approach to navigation.
          Today, organizations have far more data, yet many decisions are still
          made on instinct. Data is consulted at the outset, but rarely
          revisited.
        </p>
        <p>
          Once a decision is made, few teams look back to test their original
          assumptions against actual outcomes. This lack of a feedback loop
          means that while technology has advanced, the decision-making process
          has not.
        </p>

        <h2>Mapping the Value Chain</h2>
        <p>
          Improving decision quality starts with understanding how the business
          actually works end-to-end. Mapping the value chain makes it clear how
          decisions in one area affect others — pricing, demand, capacity,
          service levels, and financial results.
        </p>
        <p>
          To make that view usable, companies need a shared knowledge base that
          captures institutional memory:
        </p>
        <ul>
          <li>Historical outcomes and lessons learned</li>
          <li>Decision rationales and the “why” behind the choice</li>
          <li>Key assumptions and known constraints</li>
        </ul>
        <p>
          Over time, this becomes institutional memory — something most
          organizations lack. This knowledge base feeds a business control
          tower: a single view where operational, commercial, market, and
          financial data come together.
        </p>
        <p>
          The control tower does not make decisions. It provides context —
          showing trade-offs, sensitivities, and risks across the entire value
          chain.
        </p>

        <h2>Industry Spotlight: The Biofuel “Control Tower”</h2>
        <p>
          In the biofuel industry — specifically Bioethanol and Sustainable
          Aviation Fuel (SAF) — the complexity of the value chain is a prime
          candidate for an Agentic AI Architecture.
        </p>
        <p>
          Traditional software tracks data in silos. An agentic structure acts
          as connective tissue, orchestrating specialized AI agents to manage
          trade-offs between feedstock volatility, process chemistry, and carbon
          intensity (CI) regulations.
        </p>
        <p>
          Imagine a “Bio-Control Tower” where multiple specialized agents feed
          into a shared institutional knowledge base:
        </p>
        <ol>
          <li>
            <strong>The Feedstock & Market Agent:</strong> Scans global markets
            for corn or residues and cross-references spot prices with logistics
            constraints. Recommendation: Switch to Feedstock B — raw cost is 5%
            higher, but CI score is 12% lower, qualifying for higher tax
            credits.
          </li>
          <li>
            <strong>The Process & TEA Agent:</strong> Runs continuous
            techno-economic analysis to simulate sensitivity across market
            prices, regulatory changes, and energy costs. Recommendation: Adjust
            conversion parameters to prioritize low-CI output today.
          </li>
          <li>
            <strong>The CI & Regulatory Agent:</strong> Monitors RFS and LCFS
            credit prices in real time. Recommendation: Hold Batch 402 and
            capture an additional 8% margin during the projected price surge.
          </li>
        </ol>

        <h2>Why Time Savings Alone Undersell AI’s True ROI</h2>
        <p>
          Much of today’s AI ROI discussion focuses on time savings. These gains
          are real, but efficiency is only the starting point.
        </p>
        <p>
          Time saved does not automatically translate into strategic business
          impact. The true value of AI lies in helping companies make better
          decisions that drive superior economic outcomes.
        </p>

        <h2>From Productivity Gains to Economic Advantage</h2>
        <ul>
          <li>Surface hidden risks before they materialize</li>
          <li>Identify constraints that quietly erode performance</li>
          <li>Model trade-offs leaders might otherwise overlook</li>
          <li>Reveal second- and third-order effects of decisions</li>
        </ul>
        <p>
          In these cases, AI’s ROI is not measured in hours saved — but in
          losses avoided and opportunities captured.
        </p>

        <h2>Economic Impact: From FID to Daily Ops</h2>
        <div className="hv2-article__table-wrap">
          <table>
            <thead>
              <tr>
                <th>Decision Type</th>
                <th>Traditional Approach</th>
                <th>Agentic AI Approach</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Capital Planning / FID</td>
                <td>Static spreadsheets based on historical averages</td>
                <td>
                  Stochastic simulations running thousands of scenarios to
                  stress-test IRR
                </td>
              </tr>
              <tr>
                <td>Business Development</td>
                <td>Manual competitive analysis</td>
                <td>
                  Competitive intelligence agents benchmarking technologies in
                  real time
                </td>
              </tr>
              <tr>
                <td>Daily Operations</td>
                <td>Reactive adjustments from shift logs</td>
                <td>
                  Continuous learning engines matching patterns and identifying
                  fixes instantly
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <h2>Conclusion: The Goal Is Better Learning</h2>
        <p>
          Judgment and experience still matter. But when intuition is supported
          by an end-to-end view and a learning knowledge base, decisions become
          easier to explain, review, and improve.
        </p>
        <p>
          The goal is not perfect decisions — it is fewer surprises and better
          learning when they happen. This is where AI transitions from a
          cost-saving tool to a permanent competitive advantage.
        </p>
      </>
    ),
  },
  {
    id: "dsider-x-corys-partnership",
    type: "News",
    image: newsMain,
    title: "Dsider x CORYS Partnership",
    excerpt:
      "We are thrilled to announce a strategic partnership between CORYS and Dsider.",
    body: "We are thrilled to announce a strategic partnership between CORYS and Dsider, marking a transformative step in delivering next-generation solutions in navigating a path to net zero from FEL and FEED stages through commissioning and operations!\nThis MoU signifies a shared commitment to combining cutting-edge expertise in real-time dynamic process modeling with state-of-the-art techno economic decision intelligence, ensuring our clients achieve unparalleled efficiency, safety, and returns for new energy projects. Together, Dsider and CORYS will empower industries to make smarter, faster, and more informed decisions with scalable, integrated solutions.\nStay tuned for updates as we bring this partnership to life.\nThe Future of Intelligent Operations Begins Today!",
  },
  {
    id: "analytics-shaping-the-future-of-energy-transition",
    type: "News",
    image: news1,
    title: "Analytics Shaping the Future of Energy Transition",
    excerpt: "Sujatha Kumar, CEO of Dsider joins Mike on #TheGreenInsider",
    body: "Imagine one platform that provides operations forecasting, financial – risk – carbon abatement – scenario analysis, and data integration all in one location. This integrated platform can help accelerate the energy transition and do it with precision.\nMore about Sujatha\nSujatha Kumar, an entrepreneur and senior technology executive, is the Founder and CEO of Dsider. With a background in senior management roles at multiple technology companies in the energy sector, she notably led one of them to a successful sale to Honeywell. With considerable experience in both operations and digital domains, she possesses a passion for introducing innovative products and concepts to organizations. Sujatha and her team possess extensive experience in energy and industrial markets, focusing on mid to large enterprises.\nIn addition to her responsibilities at Dsider, Sujatha serves as an advisor at Knightsgate Ventures and collaborates with various startups. She also contributes her expertise as an advisor and board member to both public companies and startups.\nAbout Dsider\nDsider enterprise planning solution empowers engineers and financial planners navigate energy transition initiatives – through operational analysis, capital allocation to return on investment. Dsider streamlines the analysis for energy planning, emission calibration, resource efficiency, and financial computations in one place.\nCollaboration and forecasting features facilitate seamless planning and execution of sustainability initiatives. Dsider, a SaaS offering focused on the energy and industrial markets reduces risks and increases decision confidence while accelerating energy transition with ease and precision.",
  },
  {
    id: "empowering-sustainability-through-digital-innovation",
    type: "News",
    image: news2,
    title: "Empowering Sustainability through Digital Innovation",
    excerpt:
      "We are pleased to share that Sujatha Kumar, Founder and CEO, Dsider.",
    body: "We are pleased to share that Sujatha Kumar, Founder and CEO, Dsider.",
  },
  {
    id: "musings-on-netzero-planning-1",
    type: "News",
    image: news3,
    title: "Musings on Netzero Planning - #1",
    excerpt:
      "With mixed results on climate action, a new year always offers hope.",
    body: "With mixed results on climate action, a new year always offers hope.",
  },
  {
    id: "sustainable-decarbonized-future",
    type: "Blog",
    image: sustainableFuture,
    title:
      "The path to a sustainable decarbonized future demands a new approach, and Dsider is here to lead the way.",
    excerpt:
      "As companies across all industries navigate the energy transition, they’re facing a critical challenge",
    body: "As companies across all industries navigate the energy transition, they’re facing a critical challenge: how can they be responsible, green enterprises while continuing to deliver value to stakeholders! Connect with us info@dsider.app to have a discussion.",
    source:
      "https://www.linkedin.com/posts/sujathak-dsider_energytransition-decarbonization-sustainability-activity-7249788281259753472-W19l/?utm_source=share&utm_medium=member_ios",
  },
  {
    id: "climate-week-foak",
    type: "Blog",
    image: climateWeek,
    title:
      "With Climate Week activities in full swing and discussions on First of a Kind (FOAK) innovations ongoing,",
    excerpt:
      "It's crucial to focus on balancing emission reductions with economic sustainability for real climate impact.",
    body: "It's crucial to focus on balancing emission reductions with economic sustainability for real climate impact. Dsider FOAK solution tackles the Techno-Economic Analysis challenge, streamlining the energy transition and decarbonization process. This empowers companies to shift confidently to low-carbon products, facilitating a more precise and sustainable transformation.\nLet's work together to make the energy transition smarter! 🌱📢 Calling all startups, project developers, industrial companies, and financial institutions — together, we can drive meaningful change.",
    source:
      "https://www.linkedin.com/posts/sujathak-dsider_nyclimateweek-decarbonization-energytransition-activity-7244748108281044992-6iG9/?utm_source=share&utm_medium=member_ios",
  },
  {
    id: "techno-economic-analysis-tea",
    type: "Blog",
    image: blog2,
    title:
      "We've been highlighting the vital role of Techno-Economic Analysis (TEA) in driving the energy transition and advancing decarbonization efforts.",
    excerpt:
      "TEA is not merely a tool; it serves as a fundamental cornerstone for evaluating initiatives and new technologies.",
    body: "TEA is not merely a tool; it serves as a fundamental cornerstone for evaluating initiatives and new technologies. It ensures their feasibility, unlocks maximum economic returns, and effectively mitigates risks—ultimately guiding us toward achieving NetZero sustainably.\nHow is your organization leveraging TEA to accelerate the energy transition? Contact us to learn more or get a demo at Info@dsider.app",
    source:
      "https://www.linkedin.com/posts/sujathak-dsider_energytransition-decarbonization-technoeconomicanalysis-activity-7246889163487125504-ee8S/?utm_source=share&utm_medium=member_ios",
  },
  {
    id: "tea-for-startups-and-industry",
    type: "Blog",
    image: blog4,
    title:
      "Over the past few weeks, we’ve emphasized the value of techno-economic analysis (TEA) for startups, financial organization and industrial companies.",
    excerpt:
      "In the last 18 months, many firms have scaled back their sustainability goals.",
    body: "In the last 18 months, many firms have scaled back their sustainability goals. BP and Shell adjusted targets due to rising oil prices, while Microsoft fell short on carbon reductions with the growth of AI. Key challenges include unrealistic targets, limited returns on decarbonization, and the need to quantify future carbon costs.\nIt is possible to balance environmental benefits with returns! Success requires meaningful, actionable, and scalable efforts. Climate innovators, financial organizations, and industrial companies—let’s connect to strengthen your sustainability strategy and business model with actionable insights.",
    source:
      "https://www.linkedin.com/posts/dsider_netzero-sustainability-tea-activity-7252424899376398339-MbOb/?utm_source=share&utm_medium=member_ios",
  },
  {
    id: "houston-energy-tech-startup",
    type: "Blog",
    image: houstonStartup,
    title:
      "How this Houston energy tech startup transforms innovation into scalable success",
    excerpt:
      "What if the future of clean energy wasn’t just about invention, but execution?",
    body: "What if the future of clean energy wasn’t just about invention, but execution?\nFor Sujatha Kumar, CEO of Dsider, success in clean tech hinges on more than groundbreaking technology—it’s about empowering founders with the tools to make their innovations viable, scalable, and economically sound. Through Dsider’s techno-economic analysis (TEA) platform, Kumar is helping startups bridge the critical gap between vision and execution, ensuring they can navigate complex markets with confidence.",
    source:
      "https://www.linkedin.com/posts/dsider_dsider-houstoninnovation-greentown-activity-7240330659926306816-H48E?utm_source=share&utm_medium=member_ios",
  },
  {
    id: "aws-generative-ai-energy-lab",
    type: "Blog",
    image: awsLab,
    title:
      "Meet the startups selected for #AWS generative AI for Energy Lab announced last week during the Energy Symposium in Houston!",
    excerpt:
      "We're excited to announce that Dsider was selected for the AWS Generative AI Energy Lab out of 800+ applications from 74 countries.",
    body: "We're excited to announce that Dsider was selected for the AWS Generative AI Energy Lab out of 800+ applications from 74 countries! 🌎\nWe look forward to connecting and collaborating with AWS enterprise customers to test and deploy our generative AI climate tech solutions.",
    source:
      "https://www.linkedin.com/posts/aws-energy-utilities_aws-activity-7232062122073931776-OMlm?utm_source=share&utm_medium=member_desktop",
  },
  {
    id: "ceraweek-speaker-announcement",
    type: "News",
    image: ceraWeek,
    title: "CERAWeek agora : Speaker Announcement",
    excerpt: "Our CEO, Sujatha Kumar will be speaking at CERAWeek!",
    body: "Our CEO, Sujatha Kumar will be speaking at CERAWeek!",
  },
];

// Listing order follows the Figma design; posts not listed keep their order after.
const ORDER = [
  "ai-decision-multiplier",
  "sustainable-decarbonized-future",
  "analytics-shaping-the-future-of-energy-transition",
  "climate-week-foak",
  "tea-for-startups-and-industry",
  "houston-energy-tech-startup",
  "aws-generative-ai-energy-lab",
  "ceraweek-speaker-announcement",
  "dsider-x-corys-partnership",
];
const rank = (p) => (ORDER.includes(p.id) ? ORDER.indexOf(p.id) : ORDER.length);
POSTS.sort((a, b) => rank(a) - rank(b));

export const postPath = (post) => `/insights/${post.id}`;
